import { NavigationQueryProvider } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { identifierQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { toggleQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { Fetching } from "components/common/loading/fetching";
import { isFetchBaseQueryError } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import { useListEventLabellingModelsQuery } from "store/api/generatedApi";
import { ModelDetail } from "./ModelDetail";
import { ModelList } from "./ModelList";

const ModelDashboardPage = () => {
  const { t } = useTenantTranslation();
  return (
    <AccountPageLayout title={t("modelDashboard")}>
      <NavigationQueryProvider>
        <ModelDashboardContent />
      </NavigationQueryProvider>
    </AccountPageLayout>
  );
};

const ModelDashboardContent = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { data, isFetching, isLoading, error } =
    useListEventLabellingModelsQuery({ tenantName });
  const [showUnused, setShowUnused] = useNavigationQuery(
    "unusedModels",
    false,
    toggleQuery,
    "replace"
  );
  const [selectedModelId, setSelectedModelId] = useNavigationQuery(
    "model",
    null,
    identifierQuery,
    "push"
  );

  const visibleModels = useMemo(
    () =>
      (data?.models ?? []).filter((m) => showUnused || m.status !== "unused"),
    [data, showUnused]
  );

  // Derive the selected model from the visible list. If the selection was
  // filtered out (e.g. it was unused and the operator hid unused models),
  // the right pane shows its empty state until the model is visible again.
  // The selection id persists in state — toggling visibility back restores it.
  const selectedModel = useMemo(
    () => visibleModels.find((m) => m.id === selectedModelId) ?? null,
    [visibleModels, selectedModelId]
  );

  const unusedCount = useMemo(
    () => (data?.models ?? []).filter((m) => m.status === "unused").length,
    [data]
  );

  if (error) {
    const detail = isFetchBaseQueryError(error)
      ? typeof error.data === "string"
        ? error.data
        : JSON.stringify(error.data)
      : (error as { message?: string }).message;

    return (
      <Alert severity="error">
        {t("unknownError")}
        {detail ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {detail}
          </Typography>
        ) : null}
      </Alert>
    );
  }

  return (
    <Stack spacing={1} sx={{ flex: 1, overflow: "hidden" }}>
      <Stack direction="row" justifyContent="flex-end">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showUnused}
              onChange={(e) => {
                setShowUnused(e.target.checked);
              }}
            />
          }
          label={
            unusedCount > 0
              ? `${t("showUnused")} (${unusedCount})`
              : t("showUnused")
          }
        />
      </Stack>
      <Fetching fetching={isFetching} sx={{ flex: 1, overflow: "hidden" }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ flex: 1, height: "100%", overflow: "hidden" }}
        >
          <Paper
            variant="outlined"
            sx={{ flex: 6, minWidth: 0, overflow: "auto" }}
          >
            {isLoading ? null : (
              <ModelList
                models={visibleModels}
                selectedModelId={selectedModelId}
                onSelect={setSelectedModelId}
              />
            )}
          </Paper>
          <Paper
            variant="outlined"
            sx={{ flex: 4, minWidth: 0, overflow: "hidden", display: "flex" }}
          >
            <ModelDetail model={selectedModel} />
          </Paper>
        </Stack>
      </Fetching>
    </Stack>
  );
};

export default ModelDashboardPage;
